import { captureConvexError, createSupportReference } from "@/v2Projects/telemetry";
import type { ProjectSide } from "@/v2Projects/types";

type ErrorKind = "auth" | "permission" | "network" | "validation" | "attachment" | "unknown";

export type ProjectErrorAction =
  | "loadProject"
  | "loadMessages"
  | "loadNotifications"
  | "sendMessage"
  | "sendNote"
  | "uploadAttachment"
  | "updateNotification"
  | "markAllNotificationsRead"
  | "updateProjectStatus"
  | "createQuote"
  | "cancelQuote"
  | "rejectQuote"
  | "viewQuote"
  | "updateQuotePaymentStatus";

export interface UserErrorMessage {
  title: string;
  description: string;
  supportReference: string;
}

interface ReportProjectUserErrorOptions {
  projectId?: number | null;
  threadId?: string | null;
  side?: ProjectSide | "unknown" | null;
  extra?: Record<string, string | number | boolean | null | undefined>;
}

function getRawMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error ?? "");
}

function classifyError(error: unknown, action: ProjectErrorAction): ErrorKind {
  const message = getRawMessage(error).toLowerCase();
  if (/unauth|auth|jwt|token|session|csrf|sanctum/.test(message)) return "auth";
  if (/forbidden|permission|not allowed|access|participant|acting_org|org/.test(message)) return "permission";
  if (/network|timeout|offline|failed to fetch|connection|websocket|socket|unreachable/.test(message)) return "network";
  if (/validat|invalid|required|422|malformed|bad request/.test(message)) return "validation";
  if (action === "uploadAttachment" || /file|upload|attachment|mime|size|too large|media/.test(message)) return "attachment";
  return "unknown";
}

function withReference(message: string, supportReference: string): string {
  return `${message} Reference: ${supportReference}`;
}

export function buildProjectUserError(
  action: ProjectErrorAction,
  error: unknown,
  supportReference = createSupportReference(),
): UserErrorMessage {
  const kind = classifyError(error, action);

  if (kind === "auth") {
    return {
      title: "Your session needs to be refreshed",
      description: withReference("Sign in again before continuing. We logged the auth failure for support.", supportReference),
      supportReference,
    };
  }

  if (kind === "permission") {
    return {
      title: "You do not have access to this project action",
      description: withReference("Your account or organization is not allowed to make this change. If that looks wrong, send this reference to support.", supportReference),
      supportReference,
    };
  }

  if (kind === "network") {
    return {
      title: "The project workspace is not reachable",
      description: withReference("Your change was not confirmed because the realtime project service could not be reached.", supportReference),
      supportReference,
    };
  }

  if (kind === "validation") {
    return {
      title: "The project workspace rejected the request",
      description: withReference("Check the message, quote, status, or attachment details and send this reference to support if it still looks valid.", supportReference),
      supportReference,
    };
  }

  if (kind === "attachment") {
    return {
      title: "The attachment could not be uploaded",
      description: withReference("Check the file type and size. The file was not attached to your message.", supportReference),
      supportReference,
    };
  }

  const fallbackByAction: Record<ProjectErrorAction, string> = {
    loadProject: "We could not load this project workspace.",
    loadMessages: "We could not load the latest project conversation.",
    loadNotifications: "We could not load your project notifications.",
    sendMessage: "We could not save your message, so nothing was sent.",
    sendNote: "We could not save your note, so nothing was sent.",
    uploadAttachment: "We could not attach the selected file to your message.",
    updateNotification: "We could not update that notification state.",
    markAllNotificationsRead: "We could not mark your notifications as read.",
    updateProjectStatus: "We could not update the project status.",
    createQuote: "We could not create the quote.",
    cancelQuote: "We could not cancel the quote.",
    rejectQuote: "We could not reject the quote.",
    viewQuote: "We could not mark the quote as viewed.",
    updateQuotePaymentStatus: "We could not update the quote payment status.",
  };

  return {
    title: "We could not complete that action",
    description: withReference(fallbackByAction[action], supportReference),
    supportReference,
  };
}

const operationByAction: Record<ProjectErrorAction, string> = {
  loadProject: "project.load_failed",
  loadMessages: "chat.messages_load_failed",
  loadNotifications: "notifications.load_failed",
  sendMessage: "chat.send_message_failed",
  sendNote: "chat.send_note_failed",
  uploadAttachment: "chat.attachment_upload_failed",
  updateNotification: "notifications.update_failed",
  markAllNotificationsRead: "notifications.mark_all_read_failed",
  updateProjectStatus: "project.status_update_failed",
  createQuote: "quote.create_failed",
  cancelQuote: "quote.cancel_failed",
  rejectQuote: "quote.reject_failed",
  viewQuote: "quote.view_failed",
  updateQuotePaymentStatus: "quote.payment_status_update_failed",
};

export function reportProjectUserError(
  action: ProjectErrorAction,
  error: unknown,
  options: ReportProjectUserErrorOptions = {},
): UserErrorMessage {
  const supportReference = createSupportReference();
  captureConvexError(error, {
    operation: operationByAction[action],
    supportReference,
    projectId: options.projectId,
    threadId: options.threadId,
    side: options.side,
    level: "error",
    extra: options.extra,
  });
  return buildProjectUserError(action, error, supportReference);
}
